import React, { useEffect, useState } from 'react';
import { useLocation, useParams } from 'react-router';
import { useLazyQuery } from '@apollo/client/react';
import { SplitterPaneProps } from '@progress/kendo-react-layout';
import debounce from 'lodash/debounce';
import style from './style.module.scss';
import CoreSplitter from '@components-clean/core/core-splitter';
import { InfoField } from '@components-clean/core/info-field';
import Loading from '@components-clean/core/loading/loading';
import { IsPopOut } from '@components-clean/core/page-toolbar/page-toolbar';
import { FilterUIConfigType } from '@components-clean/core/study-data-filter/study-data-filter.interfaces';
import { usePageToolbar } from '@contexts-clean/page-toolbar-context';
import { StudyDataFilterContextProvider, useStudyDataFilterContext } from '@contexts-clean/study-data-filter-context';
import ActionBar from '@pages-clean/central-monitoring/components/action-bar/action-bar';
import DataTables from '@pages-clean/central-monitoring/data-view/datatables/datatables';
import { PanelsSection } from '@pages-clean/central-monitoring/data-view/panels/panels-section';
import { IDataset } from '@pages-clean/central-monitoring/data-view/types';
import { useLayout, useTranslation } from '@hooks/index';
import { SplitterOrientation } from '@utils/constants';
import { GET_DATA_REVIEW_TABLES, GET_DATA_VIEW_CONFIGURATION } from '@queries/central-monitoring';
import {
    dataReviewTables,
    dataReviewTablesVariables,
    dataViewConfiguration,
    dataViewConfigurationVariables,
} from '@queries/generated/central-monitoring.generated';
import { LoggedInPageProps } from '@config/routes/interfaces';

interface IPageProps {
    studyRef: string;
    dataset: IDataset;
    dataViewConfig: string;
}

interface IContentProps {
    dataset: IDataset;
    configId: string;
    studyRef: string;
}

const Content = ({ dataset, studyRef, configId }: IContentProps) => {
    const { state, dispatch: layoutDispatch } = useLayout();
    const isPopoutPage = IsPopOut();
    const { t } = useTranslation('cm');
    const [alignmentValues, setAlignmentValues] = useState<SplitterPaneProps[]>([{ size: '60%' }, {}]);
    // Set nextFetchPolicy to 'standby' to prevent cache updates from effecting this page. There are queries
    // that get a list of Data Views for datasets, and these share the same cache
    // Uses the same logic as cache-first, except this query does not automatically update when underlying field values change.
    // You can still manually update this query with refetch and updateQueries.
    const [loadConfig, { data: config, loading: configLoading, called: configCalled, error }] = useLazyQuery<
        dataViewConfiguration,
        dataViewConfigurationVariables
    >(GET_DATA_VIEW_CONFIGURATION, { fetchPolicy: 'no-cache' });

    const [
        loadStudyDatatables,
        { data: studyDataTables, loading: studyDataTablesLoading, called: studyDataTablesCalled },
    ] = useLazyQuery<dataReviewTables, dataReviewTablesVariables>(GET_DATA_REVIEW_TABLES);

    useEffect(() => {
        loadConfig({ variables: { studyId: studyRef, configId, timestamp: dataset.created } }).catch((err) =>
            window.console.error(err),
        );
        loadStudyDatatables({ variables: { studyId: studyRef, timestamp: dataset.created } }).catch((err) =>
            window.console.error(err),
        );
    }, [studyRef, configId, dataset.id]);

    // An annoying workaround. Popout Pages don't render the page nav component, which is responsible
    // for loading the data view config names and setting them in state. The Data View route object relies
    // on the names to be in state to set the breadcrumb appropriately. So for Popout pages, we need
    // to manually set and update the page breadcrumbs, when it shouldn't really be it's responsibility.
    // The real fix might be to move the responsibility of loading the data view names out of the page nav,
    // but it would have to be moved higher up, before AppLayout, and this could be more painful than this workaround
    // If the breadcrumb structure changes for the Data View route, this will also need updating. It assumes the last item
    // in the breadcrumbs is the Data View name, which should be reasonable
    useEffect(() => {
        if (isPopoutPage && config?.dataViewConfiguration) {
            const breadcrumbs = state.pageBreadcrumbs.slice(0, state.pageBreadcrumbs.length - 1);
            breadcrumbs.push(config.dataViewConfiguration.name ?? '');
            layoutDispatch({ type: 'SET_PAGE_BREADCRUMBS', pageBreadcrumbs: breadcrumbs });
        }
    }, [config]);

    const { sessionId } = useStudyDataFilterContext();
    const { dispatch: pageToolbarDispatch } = usePageToolbar();
    const location = useLocation();

    useEffect(() => {
        let route = location.pathname;
        const queryParams = new URLSearchParams(location.search);

        if (sessionId) queryParams.append('copySessionId', sessionId);
        if (queryParams.size) route = `${location.pathname}?${queryParams.toString()}`;

        pageToolbarDispatch({ type: 'setPopoutRoute', popoutRoute: route });
    }, [sessionId]);

    const configNotLoaded = configLoading || !configCalled;
    const studyDataTablesNotLoaded = studyDataTablesLoading || !studyDataTablesCalled;

    // If Error or no DataView Config or if the DataView Config was inactive for a given dataset
    if (error || (!configNotLoaded && !config?.dataViewConfiguration?.isActive))
        return (
            <div className={style.noDataViewConfig}>
                <InfoField type="Info" message={t('NoDataViewConfig')} />
            </div>
        );

    if (config?.dataViewConfiguration?.studyId && config.dataViewConfiguration.studyId !== studyRef)
        return (
            <div className={style.noDataViewConfig}>
                <InfoField type="Info" message={t('dataView.noDataViewForStudy')} />
            </div>
        );

    const dataViewConfigDTIds = config?.dataViewConfiguration?.dataTableIds ?? [];

    // Popout pages don't have the monitoring toolkit,
    // so we don't need to take that into consideration when calculating the height
    const heightOffset = isPopoutPage ? 121 : 152;

    return (
        <div style={{ height: `calc(100vh - ${heightOffset}px)` }}>
            <ActionBar
                studyRef={studyRef}
                datasetId={dataset.id}
                dataViewConfigId={configId}
                filterActions={FilterUIConfigType.DataView}
                className="k-mb-3"
            />
            {sessionId && !(configNotLoaded || studyDataTablesNotLoaded) ? (
                <CoreSplitter
                    className={style.splitterHeight}
                    orientation={SplitterOrientation.vertical}
                    alignmentValues={alignmentValues}
                    onChange={(e) => {
                        debounce(() => {
                            setAlignmentValues(e.newState);
                        }, 300);
                    }}
                    data-testid="data-view-page-splitter"
                >
                    <div data-testid="data-view-panels-pane" className="k-d-flex k-gap-2 k-flex-col">
                        {!configNotLoaded && config?.dataViewConfiguration && (
                            <PanelsSection
                                mainPanelId={config.dataViewConfiguration.mainPanelId as string}
                                panelIds={(config.dataViewConfiguration.panelIds ?? []) as string[]}
                                nonFilterablePanels={
                                    (config.dataViewConfiguration.unfilterablePanelIds ?? []) as string[]
                                }
                                studyRef={studyRef}
                                dataset={dataset}
                                configId={configId}
                            />
                        )}
                    </div>
                    <div data-testid="data-view-datatable-grid-pane" className="k-h-full">
                        {!configNotLoaded && !studyDataTablesNotLoaded && dataViewConfigDTIds.length > 0 && (
                            <DataTables
                                studyRef={studyRef}
                                studyDataTables={studyDataTables}
                                dataViewDataTableIds={dataViewConfigDTIds as string[]}
                                dataset={dataset}
                                noPadding
                                dataViewRef={configId}
                            />
                        )}
                        {!configNotLoaded && dataViewConfigDTIds.length === 0 && (
                            <div className={style.noDataViewConfig}>
                                <InfoField type="Info" message={t('dataView.datatables.noDataTable')} />
                            </div>
                        )}
                    </div>
                </CoreSplitter>
            ) : (
                <Loading />
            )}
        </div>
    );
};

const Wrapper = ({ study, cm2Dataset: dataset }: LoggedInPageProps) => {
    const { dataView } = useParams();

    if (!dataView) {
        return <span>Invalid path</span>;
    }

    const params = new URLSearchParams(window.location.search);
    const siteScore: string | undefined = params.get('sitescore') ?? undefined;
    const copySessionId: string | undefined = params.get('copySessionId') ?? undefined;
    const siteId: string | undefined = params.get('siteid') ?? undefined;
    const countryValue: string | undefined = params.get('country') ?? undefined;

    const props: IPageProps = {
        studyRef: study?.id as string,
        dataset: {
            id: dataset?.id as number,
            created: new Date(dataset?.created as Date | string | number),
            dataCutDate: new Date(dataset?.dataCutDate as Date | string),
        },
        dataViewConfig: dataView,
    };

    const preFilters = [];

    if (siteScore !== undefined)
        preFilters.push({ fieldKey: 'Site.mainPanelRAGStatus', checkedValueIds: [+siteScore] });

    if (siteId !== undefined) preFilters.push({ fieldKey: 'Site.name', checkedValueIds: [+siteId] });

    if (countryValue !== undefined) preFilters.push({ fieldKey: 'Site.country', checkedValueStrings: [countryValue] });

    return (
        <StudyDataFilterContextProvider
            studyRef={props.studyRef}
            datasetId={props.dataset.id}
            dataViewRef={props.dataViewConfig}
            key={props.dataViewConfig}
            recalculateOnDatasetChange
            preAppliedFilters={{
                datasetId: props.dataset.id,
                filters: preFilters,
            }}
            copySessionId={copySessionId}
        >
            <Content dataset={props.dataset} configId={props.dataViewConfig} studyRef={props.studyRef} />
        </StudyDataFilterContextProvider>
    );
};

export default Wrapper;
